import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Copy, Check, TrendingUp, DollarSign, MousePointerClick, Users, Award } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";

/**
 * Token-gated public stats page for affiliates. The `stats_token` is a
 * 32-hex secret emailed to the affiliate on approval — it's their private
 * link to see clicks/signups/earnings without needing a login.
 *
 * Route: /affiliate/stats?token=XXX (public — but token acts as auth)
 */
export default function AffiliateStatsPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) { setErr("Missing stats token."); return; }
    api.get(`/affiliate/stats?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || "Invalid or expired link."));
  }, [token]);

  const copy = async () => {
    if (!data?.affiliate?.share_url) return;
    const url = data.affiliate.share_url;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (err) {
        console.warn("[AffiliateStatsPage] Clipboard copy failed:", err);
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  if (err) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-6 py-20 text-center" data-testid="affiliate-stats-error">
          <h1 className="font-display text-2xl font-bold mb-2">Link not valid</h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{err}</p>
        </div>
      </AppShell>
    );
  }
  if (!data) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-6 py-20 text-center text-sm"
             style={{ color: "var(--text-secondary)" }}>Loading your stats…</div>
      </AppShell>
    );
  }
  const a = data.affiliate;
  const s = data.stats;
  const dollars = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl px-6 py-10" data-testid="affiliate-stats-page">
        <div className="mb-6">
          <div className="pill pill-ai inline-flex mb-2">
            <Award className="w-3.5 h-3.5 mr-1.5" /> {a.status === "approved" ? "Active affiliate" : a.status}
          </div>
          <h1 className="font-display text-3xl font-extrabold">Hi {a.name.split(" ")[0]}, welcome back.</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Your affiliate code: <code className="font-mono font-bold"
              style={{ background: "var(--ai-bg)", color: "var(--ai-text)", padding: "2px 6px", borderRadius: 4 }}>
              {a.code}
            </code>
          </p>
        </div>

        {/* Share URL */}
        <div className="card-surface p-5 mb-6" data-testid="affiliate-share-block">
          <div className="text-xs font-semibold uppercase tracking-wide mb-2"
               style={{ color: "var(--text-secondary)" }}>Your share link</div>
          <div className="flex items-center gap-2">
            <input className="field-input font-mono text-sm" readOnly value={a.share_url}
                   data-testid="affiliate-share-url" />
            <button type="button" className="btn-primary text-sm inline-flex items-center gap-1"
                    onClick={copy} data-testid="affiliate-copy-btn">
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>
          </div>
          <p className="text-xs mt-2" style={{ color: "var(--text-secondary)" }}>
            Share this link on your channel. Anyone who lands here through it and pays for a
            primary DS-260 case earns you a commission.
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat icon={<MousePointerClick className="w-4 h-4" />} label="Clicks" value={s.clicks}
                testId="affiliate-stat-clicks" />
          <Stat icon={<Users className="w-4 h-4" />} label="Signups" value={s.signups}
                testId="affiliate-stat-signups" />
          <Stat icon={<TrendingUp className="w-4 h-4" />} label="Paid conversions"
                value={s.paid_conversions + s.pending_conversions}
                testId="affiliate-stat-conversions" />
          <Stat icon={<DollarSign className="w-4 h-4" />} label="Total earnings"
                value={dollars(s.earnings_total_cents)} testId="affiliate-stat-total" />
        </div>

        {/* Earnings breakdown */}
        <div className="grid md:grid-cols-2 gap-4">
          <EarnBox label="Pending (owed to you)" cents={s.earnings_pending_cents}
                   count={s.pending_conversions} tone="pending"
                   testId="affiliate-earn-pending" />
          <EarnBox label="Paid out to you" cents={s.earnings_paid_cents}
                   count={s.paid_conversions} tone="paid"
                   testId="affiliate-earn-paid" />
        </div>

        <p className="text-xs mt-8" style={{ color: "var(--text-secondary)" }}>
          Bookmark this page — anyone with the URL can view your stats, so keep it private.
        </p>
      </div>
    </AppShell>
  );
}

function Stat({ icon, label, value, testId }) {
  return (
    <div className="card-surface p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide mb-1"
           style={{ color: "var(--text-secondary)" }}>
        {icon} {label}
      </div>
      <div className="font-display text-2xl font-extrabold" style={{ color: "var(--text-primary)" }}>
        {value}
      </div>
    </div>
  );
}

function EarnBox({ label, cents, count, tone, testId }) {
  const isPaid = tone === "paid";
  return (
    <div className="card-surface p-5" data-testid={testId}>
      <div className="text-xs uppercase tracking-wide mb-1"
           style={{ color: "var(--text-secondary)" }}>{label}</div>
      <div className="font-display text-3xl font-extrabold"
           style={{ color: isPaid ? "#065F46" : "var(--accent-copper)" }}>
        ${((cents || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}
      </div>
      <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
        {count} {count === 1 ? "conversion" : "conversions"}
      </div>
    </div>
  );
}
